import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConstraintExtendedClient } from '@frontend/constraint/api';
import { ConstraintExtended, ConstraintMode } from '@frontend/constraint/types';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { usePlatformModalHost } from '../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../header/overview-header';
import { useConstraints } from './constraints.controller';

export const Constraints = () => {
    const viewProps = useConstraints();
    const { openPlatformDetail } = usePlatformModalHost();

    return (
        <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOTS}</span>
                        <span>/</span>
                        <span>{CommonMessage.OBJECTS.CONSTRAINT.PLURAL}</span>
                    </div>
                }
            />
            <ReactiveObjectList<ConstraintExtended>
                id='constraints-list'
                useStaticListId
                key='constraints-list'
                fetch={(arg) => ConstraintExtendedClient.fetchExtendedConstraints(arg)}
                dispatch={viewProps.dispatch}
                onSelectElement={(obj) =>
                    openPlatformDetail({
                        entityType: EntityType.CONSTRAINT,
                        id: obj.id,
                        parents: { spot_id: obj.spot?.entity_id || obj.spot_id }
                    })
                }
                type={EntityType.CONSTRAINT}
                columns={{
                    columns: ['spot', 'module', 'slot', 'name', 'type', 'mode', 'row', 'column'],
                    customCells: new Map([
                        [
                            'spot',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[200px]'>
                                        {obj.row.original.spot ? (
                                            <PlatformObjectBadge
                                                entityType={EntityType.SPOT}
                                                id={obj.row.original.spot.entity_id}
                                                display={obj.row.original.spot.name}
                                                key={obj.row.original.spot.entity_id}
                                                foreign={obj.row.original.spot.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'module',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[200px]'>
                                        {obj.row.original.module ? (
                                            <PlatformObjectBadge
                                                entityType={EntityType.MODULE}
                                                id={obj.row.original.module.entity_id}
                                                display={obj.row.original.module.name}
                                                key={obj.row.original.module.entity_id}
                                                parentIds={{
                                                    spot_id: obj.row.original.spot?.entity_id ?? obj.row.original.module.data?.spot_id
                                                }}
                                                foreign={obj.row.original.module.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.module)}
                                            />
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'slot',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[200px]'>
                                        {obj.row.original.slot ? (
                                            <PlatformObjectBadge
                                                entityType={EntityType.SLOT}
                                                id={obj.row.original.slot.entity_id}
                                                display={obj.row.original.slot.name}
                                                key={obj.row.original.slot.entity_id}
                                                parentIds={{
                                                    spot_id:
                                                        obj.row.original.spot?.entity_id ??
                                                        obj.row.original.slot.data?.spot_id ??
                                                        obj.row.original.module?.data?.spot_id,
                                                    module_id: obj.row.original.module?.entity_id ?? obj.row.original.slot.data?.module_id
                                                }}
                                                foreign={obj.row.original.slot.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                            />
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'row',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[100px]'>
                                        {obj.row.original.row !== null && obj.row.original.row_range !== null && obj.row.original.row_range !== undefined ? (
                                            <span className='text-foreground-default'>
                                                {obj.row.original.row}-{obj.row.original.row + obj.row.original.row_range}
                                            </span>
                                        ) : obj.row.original.row !== null ? (
                                            <span className='text-foreground-default'>{obj.row.original.row}</span>
                                        ) : null}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'column',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[100px]'>
                                        {obj.row.original.column !== null &&
                                        obj.row.original.column_range !== null &&
                                        obj.row.original.column_range !== undefined ? (
                                            <span className='text-foreground-default'>
                                                {obj.row.original.column}-{obj.row.original.column + obj.row.original.column_range}
                                            </span>
                                        ) : obj.row.original.column !== null ? (
                                            <span className='text-foreground-default'>{obj.row.original.column}</span>
                                        ) : null}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'mode',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[120px]'>
                                        <DefaultBadge
                                            label={getModeLabel(obj.row.original.mode)}
                                            styles={{
                                                container: {
                                                    color: 'bg-brand-muted'
                                                }
                                            }}
                                        />
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'type',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[120px]'>
                                        <DefaultBadge
                                            label={(obj.row.original.type.charAt(0).toUpperCase() + obj.row.original.type.slice(1)).replace(/_/g, ' ')}
                                            styles={{
                                                container: {
                                                    color: 'bg-brand-muted'
                                                }
                                            }}
                                        />
                                    </div>
                                </td>
                            )
                        ]
                    ]),
                    headerOverwrite: new Map([
                        ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                        ['module_id', CommonMessage.OBJECTS.MODULE.DEFAULT],
                        ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                        ['name', CommonMessage.OBJECTS.CONSTRAINT.DEFAULT],
                        [
                            'row',
                            <FormattedMessage
                                key='constraints.rows-header'
                                id='constraints.rows-header'
                                defaultMessage='Row(s)'
                            />
                        ],
                        [
                            'column',
                            <FormattedMessage
                                key='constraints.columns-header'
                                id='constraints.columns-header'
                                defaultMessage='Column(s)'
                            />
                        ],
                        ['mode', CommonMessage.OBJECTS.COMMON_FIELDS.MODE],
                        [
                            'data',
                            <FormattedMessage
                                key='constraints.data-header'
                                id='constraints.data-header'
                                defaultMessage='Data'
                            />
                        ]
                    ])
                }}
                activeFilter={{
                    searchKeys: ['name'],
                    filterKeys: ['spot_id', 'module_id', 'slot_id'],
                    displayName: new Map([['name', CommonMessage.OBJECTS.CONSTRAINT.CONSTRAINT_NAME]])
                }}
            />
        </div>
    );
};

const getModeLabel = (mode: ConstraintMode): React.ReactNode => {
    switch (mode) {
        case ConstraintMode.ONLY:
            return (
                <FormattedMessage
                    id='constraint.mode.only'
                    defaultMessage='Only'
                />
            );
        case ConstraintMode.PREFERABLY:
            return (
                <FormattedMessage
                    id='constraint.mode.preferably'
                    defaultMessage='Prefer'
                />
            );
        case ConstraintMode.RATHER_NOT:
            return (
                <FormattedMessage
                    id='constraint.mode.rather_not'
                    defaultMessage='Avoid'
                />
            );
        case ConstraintMode.DEFINITELY_NOT:
            return (
                <FormattedMessage
                    id='constraint.mode.definitely_not'
                    defaultMessage='Never'
                />
            );
        default:
            return mode;
    }
};
